import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import type {
  IApprovalAllocationInput,
  ICustomerPurchaseOrder
} from "@/interfaces/api/customer-purchase-order.interface"
import { CustomerPurchaseOrder } from "@/models/customer-purchase-order.model"
import { CustomerPurchaseOrderRepository } from "@/repositories/customer-purchase-order-repository"
import { useCustomerPurchaseOrderStore } from "@/store/customer-purchase-orders.store"
import { useLayoutColumnsStore } from "@/store/layout-columns.store"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import { markRaw } from "vue"
import CustomerInvoiceDocument from "@/components/customer_purchase_order/CustomerInvoiceDocument.vue"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { FileRelation } from "@/models/file/file-relation.model"

const invoiceDocument = markRaw(CustomerInvoiceDocument)

export class CustomerPurchaseOrderController extends GenericModelController<
  CustomerPurchaseOrder,
  ICustomerPurchaseOrder,
  CustomerPurchaseOrderRepository
> {
  private readonly pendingDraftCreates = new Map<string, Promise<CustomerPurchaseOrder>>()

  public showInvoice(order: CustomerPurchaseOrder): void {
    FileViewerController.displayContentInColumn({
      content: invoiceDocument,
      title: order.invoiceNumber
        ? `Factuur ${order.invoiceNumber}`
        : `Factuurvoorbeeld · ${order.getSupplierName()}`,
      props: {
        approvalId: order.id!,
        refreshKey: Date.now(),
        savedInvoiceNote: order.invoiceNote
      },
      createColumnIfNone: true
    })
  }

  public refreshInvoicePreview(order: CustomerPurchaseOrder): void {
    if (order.invoiceNumber) return
    for (const column of useLayoutColumnsStore().fileViewers) {
      const props = column.props
      if (
        props?.contentOverride === invoiceDocument &&
        props.contentOverrideProps?.approvalId === order.id
      ) {
        props.contentOverrideProps.savedInvoiceNote = order.invoiceNote
      }
    }
  }

  public async createInvoice(order: CustomerPurchaseOrder): Promise<void> {
    const document = await this.apiFunctions
      .getRepository()
      .invoiceDocument(order.id!, order.approvalRevision)
    if (document.card) this.storeFunctions.updateModel(document.card)
    const files = document.files.map((file) => new FileRelation(file))
    await FileViewerController.displayFileRelationsInColumn(files, files[0] ?? null, {
      allowDelete: false,
      createColumnIfNone: true
    })
  }

  public async setDeleted(
    order: CustomerPurchaseOrder,
    value: boolean
  ): Promise<CustomerPurchaseOrder> {
    const result = await this.apiFunctions
      .getRepository()
      .setDeleted(order.id!, value, order.approvalRevision)
    return this.storeFunctions.updateModel(result)
  }

  public async setStatus(
    order: CustomerPurchaseOrder,
    field: "processed" | "invoiced",
    value: boolean
  ): Promise<CustomerPurchaseOrder> {
    const result = await this.apiFunctions
      .getRepository()
      .setStatus(order.id!, field, value, order.approvalRevision)
    return this.storeFunctions.updateModel(result)
  }

  public async createManualDraft(order: CustomerPurchaseOrder): Promise<CustomerPurchaseOrder> {
    const pending = this.pendingDraftCreates.get(order.id!)
    if (pending) return pending
    if (order.isSaved) return order
    if (!order.hasApprovalContent()) throw new Error("Vul eerst de akkoordkaart in.")

    const request = this.persistManualDraft(order)
    this.pendingDraftCreates.set(order.id!, request)
    try {
      return await request
    } finally {
      this.pendingDraftCreates.delete(order.id!)
    }
  }

  private async persistManualDraft(order: CustomerPurchaseOrder): Promise<CustomerPurchaseOrder> {
    // Apply server metadata only: typing can continue while the first save is in flight.
    const result = await this.apiFunctions.createModel(order.copy())
    if (!result.isSaved) throw new Error("De akkoordkaart kon niet worden opgeslagen.")
    order.isSaved = true
    order.createdAt = result.createdAt
    order.updatedAt = result.updatedAt
    order.approvalRevision = result.approvalRevision ?? 0
    order.approvalRequirements = result.approvalRequirements
    order.approvalHistory = result.approvalHistory ?? []
    const store = useCustomerPurchaseOrderStore()
    if (store.manualDraft?.id === order.id) store.manualDraft = null
    if (!store.customerPurchaseOrders.some((item) => item.id === order.id)) {
      store.customerPurchaseOrders.unshift(order)
    }
    return order
  }

  public async process(
    order: CustomerPurchaseOrder,
    allocations: IApprovalAllocationInput[]
  ): Promise<CustomerPurchaseOrder> {
    const result = await this.apiFunctions
      .getRepository()
      .process(order.id!, allocations, order.approvalRevision)
    return this.storeFunctions.updateModel(result)
  }

  constructor() {
    const customerPurchaseOrderStore = useCustomerPurchaseOrderStore()
    const { customerPurchaseOrders } = storeToRefs(customerPurchaseOrderStore)

    super(
      CustomerPurchaseOrder,
      { collection: customerPurchaseOrders },
      new CustomerPurchaseOrderRepository()
    )

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      CustomerPurchaseOrder,
      customerPurchaseOrders
    )
  }
}
